import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';

import { loadPdf } from '@/lib/pdf/pdf';

import { PdfPage } from './PdfPage';
import styles from './PdfViewer.module.css';

export interface PdfViewerProps {
  /** A PDF the browser may fetch — a signed URL is fine. */
  url: string;
  title: string;
}

const DEFAULT_RATIO = 1.414;
// Breathing room either side of a fit-to-width page.
const GUTTER = 32;

type Loaded =
  | { state: 'loading' }
  | { state: 'failed'; message: string }
  | { state: 'ready'; doc: PDFDocumentProxy; ratio: number };

type Outcome = Exclude<Loaded, { state: 'loading' }> & { key: string };

/**
 * A PDF drawn with pdf.js, as app-web draws one: every page on a canvas in one
 * scrolling column, its text selectable, with a page counter.
 *
 * It fetches its own `url` the way an `<img>` does, which is the one thing a
 * component here otherwise never does; the URL is still its whole state.
 */
export function PdfViewer({ url, title }: PdfViewerProps) {
  // A signed URL is re-minted on every read of the file; the object behind it is what matters.
  const key = objectKey(url);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const loaded: Loaded = outcome?.key === key ? outcome : { state: 'loading' };
  const [paneWidth, setPaneWidth] = useState(0);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const latestUrl = useRef(url);
  const [current, setCurrent] = useState(1);

  useEffect(() => {
    latestUrl.current = url;
  }, [url]);

  useEffect(() => {
    const abort = new AbortController();
    let opened: PDFDocumentProxy | undefined;
    loadPdf(latestUrl.current, abort.signal)
      .then(async (doc) => {
        opened = doc;
        const first = await doc.getPage(1);
        const { width, height } = first.getViewport({ scale: 1 });
        if (!abort.signal.aborted) {
          setOutcome({ key, state: 'ready', doc, ratio: height / width || DEFAULT_RATIO });
          setCurrent(1);
        }
      })
      .catch((error: unknown) => {
        if (!abort.signal.aborted) {
          setOutcome({
            key,
            state: 'failed',
            message: error instanceof Error ? error.message : String(error),
          });
        }
      });
    return () => {
      abort.abort();
      void opened?.loadingTask.destroy();
    };
  }, [key]);

  useEffect(() => {
    if (!scroller || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) =>
      setPaneWidth(Math.floor(entries[0].contentRect.width)),
    );
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scroller]);

  const onScroll = useCallback(() => {
    if (!scroller) {
      return;
    }
    // The page under the top third of the pane is the one being read.
    const line = scroller.getBoundingClientRect().top + scroller.clientHeight / 3;
    const pages = scroller.querySelectorAll<HTMLElement>('[data-page]');
    for (const page of pages) {
      if (page.getBoundingClientRect().bottom > line) {
        setCurrent(Number(page.dataset.page));
        return;
      }
    }
  }, [scroller]);

  const pageCount = loaded.state === 'ready' ? loaded.doc.numPages : 0;
  const pageWidth = Math.max(0, paneWidth - GUTTER);

  return (
    <div className={styles.viewer}>
      <div className={styles.bar}>
        <span aria-live="polite" className={styles.counter}>
          {pageCount ? `Page ${current} of ${pageCount}` : ''}
        </span>
      </div>

      <div
        aria-label={title}
        className={styles.scroller}
        onScroll={onScroll}
        ref={setScroller}
        role="document"
      >
        {loaded.state === 'loading' ? (
          <p className={styles.note} role="status">
            Loading document…
          </p>
        ) : loaded.state === 'failed' ? (
          <p className={styles.note} role="alert">
            Could not display the PDF: {loaded.message}{' '}
            <a href={url} rel="noreferrer" target="_blank">
              Open it directly
            </a>
          </p>
        ) : (
          <div className={styles.pages}>
            {Array.from({ length: pageCount }, (_, index) => (
              <PdfPage
                doc={loaded.doc}
                key={index + 1}
                pageNumber={index + 1}
                ratio={loaded.ratio}
                root={scroller}
                width={pageWidth}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function objectKey(url: string): string {
  try {
    const { origin, pathname } = new URL(url, window.location.href);
    return `${origin}${pathname}`;
  } catch {
    return url;
  }
}
